import { useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { siteMeta, categories } from "@/mocks/articles";

function BackToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 500);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (!show) return null;
  return (
    <button
      type="button"
      aria-label="回到顶部"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className="fixed bottom-6 right-5 z-50 flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 text-background-50 shadow-lg shadow-primary-500/30 transition-colors hover:bg-primary-600"
    >
      <i className="ri-arrow-up-line text-xl"></i>
    </button>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const today = "2026年9月9日 · 星期三";

  const nav: { to: string; label: string }[] = [
    { to: "/", label: "首页" },
    ...categories.map((c) => ({ to: `/category/${c.key}`, label: c.name })),
    { to: "/about", label: "关于我们" },
    { to: "/faq", label: "常见问题" },
  ];

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header>
      {/* 顶栏 */}
      <div className="bg-foreground-950 text-background-100">
        <div className="mx-auto flex h-9 max-w-6xl items-center justify-between px-4 text-[13px] md:px-6">
          <div className="flex items-center gap-2">
            <i className="ri-flashlight-fill text-accent-400"></i>
            <span className="hidden sm:inline">持续收录明星、网红、商界与公共人物的动态与热议盘点</span>
            <span className="sm:hidden">人物热议盘点站</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden items-center gap-1 md:flex">
              <i className="ri-calendar-line"></i>
              {today}
            </span>
            <Link
              to="/contact"
              className="flex items-center gap-1 whitespace-nowrap text-accent-300 transition-colors hover:text-accent-200"
            >
              <i className="ri-mail-send-line"></i>
            读者联络
            </Link>
          </div>
        </div>
      </div>

      {/* 报头 */}
      <div className="border-b border-foreground-900/20">
        <div className="mx-auto flex max-w-6xl flex-col items-center px-4 pb-4 pt-5 md:px-6 md:pb-5">
          <p className="font-label text-xs tracking-[0.35em] text-primary-600">
            {siteMeta.en} · 都市资讯
          </p>
          <h1 className="mt-1 text-center font-heading text-4xl font-black leading-none text-foreground-950 md:text-5xl">
            {siteMeta.brand.slice(0, 2)}
            <span className="text-primary-500">{siteMeta.brand.slice(2)}</span>
          </h1>
          <div className="mt-2 flex items-center gap-3 text-[13px] text-foreground-600">
            <span className="hidden h-px w-8 bg-foreground-900/40 sm:block"></span>
            <p className="whitespace-nowrap">{siteMeta.tagline}</p>
            <span className="hidden h-px w-8 bg-foreground-900/40 sm:block"></span>
          </div>
        </div>
      </div>

      {/* 主导航 */}
      <nav className="sticky top-0 z-40 border-y border-foreground-900/15 bg-background-50/95 backdrop-blur">
        <div className="mx-auto flex h-12 max-w-6xl items-center justify-between px-4 md:px-6">
          <button
            type="button"
            aria-label="打开菜单"
            className="flex h-9 w-9 items-center justify-center rounded-md border border-foreground-900/20 text-foreground-900 md:hidden"
            onClick={() => setOpen((v) => !v)}
          >
            <i className={open ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
          </button>
          <ul className="hidden items-center gap-1 md:flex">
            {nav.map((item) => {
              const active = isActive(item.to);
              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className={`flex h-12 items-center whitespace-nowrap border-b-2 px-3 text-[15px] transition-colors ${
                      active
                        ? "border-primary-500 font-bold text-primary-700"
                        : "border-transparent text-foreground-800 hover:text-primary-700"
                    }`}
                  >
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>
          <Link
            to="/contact"
            className="ml-auto hidden items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:flex"
          >
            <i className="ri-add-line"></i>
            线索投递
          </Link>
          <span className="md:hidden"></span>
        </div>
        {/* 移动端菜单 */}
        {open && (
          <div className="border-t border-foreground-900/10 bg-background-50 px-4 pb-4 md:hidden">
            <ul className="flex flex-col">
              {nav.map((item) => (
                <li key={item.to} className="border-b border-foreground-900/10 last:border-0">
                  <Link
                    to={item.to}
                    onClick={() => setOpen(false)}
                    className="block py-3 text-[15px] text-foreground-900"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
              <li className="pt-3">
                <Link
                  to="/contact"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50"
                >
                  <i className="ri-add-line"></i>
                  线索投递 / 联系我们
                </Link>
              </li>
            </ul>
          </div>
        )}
      </nav>
    </header>
  );
}

function Footer() {
  return (
    <footer className="mt-16 border-t-4 border-double border-foreground-900/20 bg-background-100">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-4 py-12 md:grid-cols-4 md:px-6">
        <div className="md:col-span-2">
          <p className="font-heading text-3xl font-black text-foreground-950">
            每日<span className="text-primary-500">黑料</span>
          </p>
          <p className="mt-1 font-label text-xs tracking-[0.3em] text-primary-600">{siteMeta.en}</p>
          <p className="mt-4 max-w-md text-sm leading-6 text-foreground-700">
            以严谨核实的编辑标准，长期盘点明星、网红、商界人物与公共人物的动态走向与热议话题。坚持「先核实、再盘点」，
            对存疑信息标注来源与状态，尊重隐私、反对造谣，努力让每一次围观都更有质量。
          </p>
          <div className="mt-5 flex items-center gap-3">
            <a href="#" rel="nofollow" aria-label="微博" className="flex h-9 w-9 items-center justify-center rounded-full border border-foreground-900/20 text-foreground-700 transition-colors hover:bg-primary-500 hover:text-background-50 hover:border-primary-500">
              <i className="ri-weibo-fill text-lg"></i>
            </a>
            <a href="#" rel="nofollow" aria-label="微信" className="flex h-9 w-9 items-center justify-center rounded-full border border-foreground-900/20 text-foreground-700 transition-colors hover:bg-primary-500 hover:text-background-50 hover:border-primary-500">
              <i className="ri-wechat-fill text-lg"></i>
            </a>
            <a href="#" rel="nofollow" aria-label="抖音" className="flex h-9 w-9 items-center justify-center rounded-full border border-foreground-900/20 text-foreground-700 transition-colors hover:bg-primary-500 hover:text-background-50 hover:border-primary-500">
              <i className="ri-music-2-fill text-lg"></i>
            </a>
            <a href="#" rel="nofollow" aria-label="知乎" className="flex h-9 w-9 items-center justify-center rounded-full border border-foreground-900/20 text-foreground-700 transition-colors hover:bg-primary-500 hover:text-background-50 hover:border-primary-500">
              <i className="ri-zhihu-fill text-lg"></i>
            </a>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-bold uppercase tracking-wider text-foreground-950">栏目盘点</h3>
          <ul className="mt-4 space-y-2.5 text-sm text-foreground-700">
            {categories.map((c) => (
              <li key={c.key}>
                <Link to={`/category/${c.key}`} className="transition-colors hover:text-primary-700">
                  {c.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="text-sm font-bold uppercase tracking-wider text-foreground-950">关于本站</h3>
          <ul className="mt-4 space-y-2.5 text-sm text-foreground-700">
            <li><Link to="/about" className="transition-colors hover:text-primary-700">关于我们</Link></li>
            <li><Link to="/faq" className="transition-colors hover:text-primary-700">常见问题</Link></li>
            <li><Link to="/contact" className="transition-colors hover:text-primary-700">读者联络</Link></li>
            <li>
              <a href={`mailto:${siteMeta.email}`} className="transition-colors hover:text-primary-700">
                {siteMeta.email}
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-foreground-900/10 bg-background-200/70">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-2 px-4 py-4 text-xs text-foreground-600 sm:flex-row md:px-6">
          <p>© 2026 {siteMeta.brand} 版权所有 · {siteMeta.icp}</p>
          <p className="flex items-center gap-1">
            <i className="ri-shield-check-line text-primary-600"></i>
            内容依据公开信息编辑，不构成对任何个人的定论
          </p>
        </div>
      </div>
    </footer>
  );
}

export default function SiteLayout() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [location.pathname]);

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}